import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Card from '../templates/ui/Card'
import Button from '../templates/ui/Button'
import CenterPopup from '../templates/ui/CenterPopup'
import CenterToast from '../templates/ui/CenterToast'
import UserAvatar from '../templates/ui/UserAvatar'
import { useCampaignStore } from '../app/store/campaignStore'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { muteluApi } from '../services/muteluApi'
import { usePartyStatus } from '../hooks/usePartyStatus'
import shareHelper from '../utils/shareHelper'
import { buildMuteluPartyJoinShareUrl } from '../utils/muteluLiffUrl'
import { buildApiErrorPopupTemplate } from '../templates/popupTemplate'
import { logPageApiCall, logPageApiError, logPageApiResult } from '../utils/apiDebugLogger'
import lineOfficialIcon from '../../../assets/images/game-mutelu/line-app-logo.png'
import './MultiplyWealth.css'

const getGroupCode = (payload) =>
  payload?.group_code ||
  payload?.group?.group_code ||
  payload?.data?.group_code ||
  payload?.data?.group?.group_code ||
  null

const normalizeDisplayName = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized) return ''
  if (normalized === 'ผู้เล่น') return ''
  return normalized
}
const getDisplayName = (member, fallback) =>
  normalizeDisplayName(member?.display_name) || normalizeDisplayName(member?.name) || fallback
const getPictureUrl = (member) => member?.picture_url || member?.pictureUrl || ''
const normalizeMember = (member) => ({
  ...member,
  is_owner: Boolean(member?.is_owner || member?.isOwner || member?.owner || member?.role === 'owner')
})
const extractMembers = (payload) => {
  const candidates = [payload?.data?.members, payload?.members, payload?.data]
  const list = candidates.find((entry) => Array.isArray(entry))
  return Array.isArray(list) ? list.map(normalizeMember) : []
}
const normalizeMessage = (value, fallback = '') => {
  if (typeof value === 'string') return value
  if (value == null) return fallback
  if (typeof value === 'object') {
    if (typeof value.message === 'string') return value.message
    try {
      return JSON.stringify(value)
    } catch {
      return fallback
    }
  }
  return String(value)
}
const normalizeLineIdKey = (value) => String(value || '').trim().toLowerCase()
const CONNECTION_ERROR_MESSAGE = 'ไม่สามารถเชื่อมต่อกับระบบ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต'
const isConnectionError = (message) => /ไม่สามารถเชื่อมต่อ|ตรวจสอบอินเทอร์เน็ต|network|timeout/i.test(String(message || ''))

export default function PartyJoin() {
  const { state } = useCampaignStore()
  const navigate = useNavigate()
  const { groupCode: groupCodeFromPath } = useParams()
  const location = useLocation()
  const [activeGroupCode, setActiveGroupCode] = useState('')
  const [isJoining, setIsJoining] = useState(true)
  const [joinMessage, setJoinMessage] = useState('')
  const [joinError, setJoinError] = useState('')
  const [joinErrorCode, setJoinErrorCode] = useState('')
  const [centerToast, setCenterToast] = useState('')
  const [groupMembers, setGroupMembers] = useState([])
  const [apiPopupTemplate, setApiPopupTemplate] = useState(null)
  const [rewardPopupTemplate, setRewardPopupTemplate] = useState(null)
  const [redirectToActivityOnPopupConfirm, setRedirectToActivityOnPopupConfirm] = useState(false)
  const [isPendingRegistration, setIsPendingRegistration] = useState(false)
  const lastApiErrorRef = useRef('')
  const centerToastTimerRef = useRef(null)
  const alreadyInPartyRedirectTimerRef = useRef(null)
  const localProfile = useMemo(
    () => ({
      lineId: localStorage.getItem('userId') || '',
      displayName: localStorage.getItem('line_display_name') || '',
      pictureUrl: localStorage.getItem('line_picture_url') || ''
    }),
    []
  )
  const requestedGroupCode = useMemo(() => {
    if (groupCodeFromPath) return groupCodeFromPath
    const searchParams = new URLSearchParams(location.search)
    return searchParams.get('groupCode') || ''
  }, [groupCodeFromPath, location.search])
  const { groupData, error: groupError, loading: groupStatusLoading } = usePartyStatus(activeGroupCode, false)

  const openApiErrorPopup = useCallback((title, subtitle, message) => {
    const normalized = normalizeMessage(message, 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
    if (!normalized) return
    if (isConnectionError(normalized)) {
      setCenterToast(CONNECTION_ERROR_MESSAGE)
      if (centerToastTimerRef.current) {
        window.clearTimeout(centerToastTimerRef.current)
      }
      centerToastTimerRef.current = window.setTimeout(() => setCenterToast(''), 1700)
      return
    }
    if (lastApiErrorRef.current === normalized) return
    lastApiErrorRef.current = normalized
    setRedirectToActivityOnPopupConfirm(false)
    setApiPopupTemplate(
      buildApiErrorPopupTemplate({
        title,
        subtitle,
        message: normalized
      })
    )
  }, [])

  const clearAlreadyInPartyRedirectTimer = useCallback(() => {
    if (alreadyInPartyRedirectTimerRef.current) {
      window.clearTimeout(alreadyInPartyRedirectTimerRef.current)
      alreadyInPartyRedirectTimerRef.current = null
    }
  }, [])

  const openAlreadyInPartyPopup = useCallback(() => {
    clearAlreadyInPartyRedirectTimer()
    setRedirectToActivityOnPopupConfirm(true)
    setApiPopupTemplate(
      buildApiErrorPopupTemplate({
        title: 'คุณอยู่ในปาร์ตี้นี้แล้ว',
        subtitle: '',
        message: 'กดปุ่มด้านล่างเพื่อทำ activity ถัดไป'
      })
    )
    alreadyInPartyRedirectTimerRef.current = window.setTimeout(() => {
      navigate('/game/mutelu/activity', { replace: true })
    }, 3000)
  }, [clearAlreadyInPartyRedirectTimer, navigate])

  useEffect(() => {
    let mounted = true

    const bootstrap = async () => {
      setIsJoining(true)
      setJoinError('')
      setJoinErrorCode('')
      setJoinMessage('')

      try {
        muteluApi.initPlayerOnce().then(() => {
          logPageApiResult('PartyJoin', 'initPlayer', { success: true })
        }).catch((error) => {
          logPageApiError('PartyJoin', 'initPlayer', error)
        })

        if (requestedGroupCode) {
          let statusDataHint = null
          try {
            logPageApiCall('PartyJoin', 'getPartyStatus pre-check', { groupCode: requestedGroupCode })
            const statusResponse = await muteluApi.getPartyStatus(requestedGroupCode)
            logPageApiResult('PartyJoin', 'getPartyStatus pre-check', statusResponse)
            statusDataHint =
              statusResponse?.data && typeof statusResponse.data === 'object'
                ? statusResponse.data
                : statusResponse || null

            const normalizedLineId = normalizeLineIdKey(localProfile.lineId)
            const membersFromStatus = Array.isArray(statusDataHint?.members) ? statusDataHint.members : []
            const isOwnerByLineId =
              normalizedLineId &&
              normalizeLineIdKey(statusDataHint?.owner?.line_id) === normalizedLineId
            const isMemberByLineId = membersFromStatus.some((member) => {
              const lineIdCandidates = [
                member?.line_id,
                member?.line_mid,
                member?.lineId,
                member?.user_id
              ]
                .filter(Boolean)
                .map((value) => normalizeLineIdKey(value))
              return normalizedLineId && lineIdCandidates.includes(normalizedLineId)
            })
            if (mounted && (isOwnerByLineId || isMemberByLineId)) {
              setActiveGroupCode(requestedGroupCode)
              setJoinError('')
              setJoinErrorCode(isOwnerByLineId ? 'OWNER_CANNOT_JOIN_OWN_PARTY' : 'ALREADY_IN_THIS_PARTY')
              openAlreadyInPartyPopup()
              return
            }
          } catch (statusError) {
            logPageApiError('PartyJoin', 'getPartyStatus pre-check', statusError)
          }

          try {
            logPageApiCall('PartyJoin', 'getMyOwnedParty pre-check', { groupCode: requestedGroupCode })
            const ownedPartyResponse = await muteluApi.getMyOwnedParty()
            logPageApiResult('PartyJoin', 'getMyOwnedParty pre-check', ownedPartyResponse)
            const ownedGroupCode = getGroupCode(ownedPartyResponse)
            if (
              mounted &&
              ownedGroupCode &&
              String(ownedGroupCode).trim().toUpperCase() === String(requestedGroupCode).trim().toUpperCase()
            ) {
              setActiveGroupCode(ownedGroupCode)
              setJoinError('')
              setJoinErrorCode('OWNER_CANNOT_JOIN_OWN_PARTY')
              openAlreadyInPartyPopup()
              return
            }
          } catch (ownedError) {
            logPageApiError('PartyJoin', 'getMyOwnedParty pre-check', ownedError)
          }

          try {
            logPageApiCall('PartyJoin', 'joinParty', { groupCode: requestedGroupCode })
            const joinResponse = await muteluApi.joinParty(requestedGroupCode)
            logPageApiResult('PartyJoin', 'joinParty', joinResponse)
            const needsRegistration =
              joinResponse?.success === false &&
              (joinResponse?.needs_registration || joinResponse?.data?.needs_registration)
            if (needsRegistration) {
              if (!mounted) return
              setActiveGroupCode(requestedGroupCode)
              setJoinError('')
              setJoinErrorCode('BIGPOINT_REGISTRATION_REQUIRED')
              setIsPendingRegistration(true)
              setJoinMessage('ระบบบันทึกรายการรอเข้าร่วมไว้แล้ว')
              openApiErrorPopup(
                'ต้องลงทะเบียน BigPoint ก่อน',
                'หน้า Party Join',
                joinResponse?.message || 'กรุณาลงทะเบียน BigPoint แล้วลองเข้าร่วมอีกครั้ง'
              )
              return
            }
            const joinedCode = getGroupCode(joinResponse) || requestedGroupCode
            if (!mounted) return
            setIsPendingRegistration(false)
            setActiveGroupCode(joinedCode)
            setJoinMessage('เข้าร่วมตี้สำเร็จ')
            const completionReward =
              joinResponse?.data?.completion_reward ||
              joinResponse?.completion_reward ||
              null
            if (completionReward?.granted) {
              const rewardAmount = Number(completionReward?.amount || 3)
              setRewardPopupTemplate({
                icon: '🎉',
                title: `ได้รับตั๋วทอง คนละ ${rewardAmount} ใบ`,
                subtitle: '',
                message: '',
                confirmLabel: 'ตกลง',
                cancelLabel: ''
              })
            }
          } catch (error) {
            logPageApiError('PartyJoin', 'joinParty', error)
            if (!mounted) return
            const errorMessage = normalizeMessage(error?.data?.message || error?.message, 'ไม่สามารถเข้าร่วมตี้ได้')
            const errorCode = String(error?.data?.error_code || error?.errorCode || '')
            if (
              errorCode === 'OWNER_CANNOT_JOIN_OWN_PARTY' ||
              errorCode === 'ALREADY_IN_THIS_PARTY' ||
              errorCode === 'LINE_ID_ALREADY_IN_GROUP'
            ) {
              setIsPendingRegistration(false)
              setActiveGroupCode(requestedGroupCode)
              setJoinError('')
              setJoinErrorCode(errorCode)
              try {
                logPageApiCall('PartyJoin', 'getPartyStatus hint', { groupCode: requestedGroupCode })
                const partyStatusResponse = await muteluApi.getPartyStatus(requestedGroupCode)
                logPageApiResult('PartyJoin', 'getPartyStatus hint', partyStatusResponse)
                openAlreadyInPartyPopup()
              } catch (statusError) {
                logPageApiError('PartyJoin', 'getPartyStatus hint', statusError)
                openAlreadyInPartyPopup()
              }
              return
            }
            if (!/GROUP_NOT_FOUND|ไม่พบปาร์ตี้/i.test(String(errorMessage))) {
              setActiveGroupCode(requestedGroupCode)
            }
            setJoinError(isConnectionError(errorMessage) ? '' : errorMessage)
            setJoinErrorCode(errorCode)
            setIsPendingRegistration(false)
            openApiErrorPopup('เข้าร่วมปาร์ตี้ไม่สำเร็จ', 'หน้า Party Join', error?.data?.message || error?.message || 'ไม่สามารถเข้าร่วมปาร์ตี้ได้')
          }
        } else {
          logPageApiCall('PartyJoin', 'getMyJoinedParties')
          const joinedGroupsResponse = await muteluApi.getMyJoinedParties()
          logPageApiResult('PartyJoin', 'getMyJoinedParties', joinedGroupsResponse)
          const joinedGroups = Array.isArray(joinedGroupsResponse?.data) ? joinedGroupsResponse.data : []
          const preferredGroup =
            joinedGroups.find((group) => group?.status === 'active') ||
            joinedGroups.find((group) => group?.status === 'completed') ||
            joinedGroups[0]
          const detectedCode = getGroupCode(preferredGroup)
          if (mounted && detectedCode) {
            setActiveGroupCode(detectedCode)
          }
        }
      } catch (error) {
        logPageApiError('PartyJoin', 'bootstrap', error)
        if (!mounted) return
        const errorMessage = normalizeMessage(error?.data?.message || error?.message, CONNECTION_ERROR_MESSAGE)
        setJoinError(isConnectionError(errorMessage) ? '' : errorMessage)
        setJoinErrorCode(String(error?.data?.error_code || error?.errorCode || ''))
        openApiErrorPopup('เชื่อมต่อไม่สำเร็จ', 'หน้า Party Join', error?.data?.message || error?.message || 'ไม่สามารถเชื่อมต่อข้อมูลปาร์ตี้ได้')
      } finally {
        if (mounted) setIsJoining(false)
      }
    }

    bootstrap()
    return () => {
      mounted = false
    }
  }, [requestedGroupCode, localProfile.lineId, openAlreadyInPartyPopup, openApiErrorPopup])

  useEffect(() => {
    if (!requestedGroupCode || !isPendingRegistration) return undefined

    let active = true
    const tryResolvePendingJoin = async () => {
      try {
        logPageApiCall('PartyJoin', 'resolvePendingPartyJoin', { groupCode: requestedGroupCode })
        const response = await muteluApi.resolvePendingPartyJoin(true)
        logPageApiResult('PartyJoin', 'resolvePendingPartyJoin', response)
        if (!active) return

        const payload = response?.data && typeof response.data === 'object' ? response.data : null
        if (payload?.resolved && payload?.joined_party) {
          const joinedCode = getGroupCode(payload.joined_party) || requestedGroupCode
          setIsPendingRegistration(false)
          setJoinError('')
          setJoinErrorCode('')
          setActiveGroupCode(joinedCode)
          setJoinMessage('ระบบเข้าร่วมตี้ให้อัตโนมัติแล้ว')
        }
      } catch (error) {
        logPageApiError('PartyJoin', 'resolvePendingPartyJoin', error)
      }
    }

    tryResolvePendingJoin()
    const intervalId = window.setInterval(tryResolvePendingJoin, 4000)
    return () => {
      active = false
      window.clearInterval(intervalId)
    }
  }, [isPendingRegistration, requestedGroupCode])

  useEffect(() => {
    if (!joinMessage) return undefined
    const timeoutId = setTimeout(() => setJoinMessage(''), 2500)
    return () => clearTimeout(timeoutId)
  }, [joinMessage])

  useEffect(() => {
    return () => {
      if (centerToastTimerRef.current) {
        window.clearTimeout(centerToastTimerRef.current)
      }
      if (alreadyInPartyRedirectTimerRef.current) {
        window.clearTimeout(alreadyInPartyRedirectTimerRef.current)
      }
    }
  }, [])

  const showCenterToast = useCallback((message) => {
    setCenterToast(message)
    if (centerToastTimerRef.current) {
      window.clearTimeout(centerToastTimerRef.current)
    }
    centerToastTimerRef.current = window.setTimeout(() => {
      setCenterToast('')
    }, 1700)
  }, [])

  useEffect(() => {
    let mounted = true

    const fetchMembers = async () => {
      if (!activeGroupCode) {
        if (mounted) setGroupMembers([])
        return
      }

      const statusMembers = Array.isArray(groupData?.members) ? groupData.members.map(normalizeMember) : []
      if (statusMembers.length > 0) {
        if (mounted) setGroupMembers(statusMembers)
        return
      }

      const normalizedGroupError = String(groupError || '')
      if (/GROUP_NOT_FOUND|ไม่พบปาร์ตี้/i.test(normalizedGroupError)) {
        if (mounted) setGroupMembers([])
        return
      }

      if (groupStatusLoading) return

      try {
        logPageApiCall('PartyJoin', 'getPartyMembers', { groupCode: activeGroupCode })
        const response = await muteluApi.getPartyMembers(activeGroupCode)
        logPageApiResult('PartyJoin', 'getPartyMembers', response)
        if (!mounted) return
        setGroupMembers(extractMembers(response))
      } catch (error) {
        logPageApiError('PartyJoin', 'getPartyMembers', error)
        if (mounted) {
          setGroupMembers([])
          openApiErrorPopup('โหลดสมาชิกไม่สำเร็จ', 'หน้า Party Join', error?.data?.message || error?.message || 'ไม่สามารถโหลดสมาชิกปาร์ตี้ได้')
        }
      }
    }

    fetchMembers()
    return () => {
      mounted = false
    }
  }, [activeGroupCode, groupData?.members, groupError, groupStatusLoading, openApiErrorPopup])

  const members = useMemo(() => {
    if (groupMembers.length > 0) return groupMembers
    return Array.isArray(groupData?.members) ? groupData.members.map(normalizeMember) : []
  }, [groupMembers, groupData?.members])
  const ownerMember = useMemo(() => members.find((member) => member?.is_owner) || null, [members])
  const nonOwnerMembers = useMemo(() => members.filter((member) => !member?.is_owner), [members])
  const selfMember = useMemo(
    () =>
      nonOwnerMembers.find((member) => {
        const ids = [member?.line_mid, member?.line_id, member?.lineId, member?.player_id, member?.user_id].filter(Boolean)
        const localLineId = normalizeLineIdKey(localProfile.lineId)
        return ids.some((id) => normalizeLineIdKey(id) === localLineId)
      }) || null,
    [nonOwnerMembers, localProfile.lineId]
  )
  const topOwnerPicture = getPictureUrl(ownerMember)
  const ownerName = getDisplayName(ownerMember, localProfile.displayName || '')
  const ownerNameWithRole = ownerName ? `${ownerName} (หัวหน้าตี้)` : '(หัวหน้าตี้)'
  const centerPicture = getPictureUrl(selfMember) || localProfile.pictureUrl
  const centerName = getDisplayName(selfMember, `${localProfile.displayName}`)
  const sideGuests = useMemo(() => nonOwnerMembers.filter((member) => member !== selfMember).slice(0, 2), [nonOwnerMembers, selfMember])
  const guestLeft = sideGuests[0] || null
  const guestRight = sideGuests[1] || null
  const membersCount =
    Number(groupData?.members_count || 0) ||
    groupMembers.length ||
    members.length ||
    (activeGroupCode ? 1 : 0)
  const hasPartyData = Boolean(groupData?.group_code || activeGroupCode || membersCount > 0)
  const shareUrl = buildMuteluPartyJoinShareUrl(activeGroupCode)

  useEffect(() => {
    if (!groupError) return
    if (hasPartyData) {
      logPageApiError('PartyJoin', 'group status fallback with existing party data', { message: groupError })
      return
    }
    openApiErrorPopup('โหลดสถานะปาร์ตี้ไม่สำเร็จ', 'หน้า Party Join', groupError)
  }, [groupError, hasPartyData, openApiErrorPopup])

  const onShareLine = async () => {
    if (!activeGroupCode) {
      setJoinError('ยังไม่พบกลุ่มสำหรับแชร์')
      return
    }
    try {
      const result = await shareHelper.smartShare(activeGroupCode, shareUrl)
      if (result?.success === false) {
        showCenterToast('share link แชร์ไม่สำเร็จ กรุณาลองอีกครั้ง')
        return
      }
      showCenterToast('กำลังเปิด LINE เพื่อแชร์')
      setJoinError('')
    } catch {
      showCenterToast('share link แชร์ไม่สำเร็จ กรุณาลองอีกครั้ง')
    }
  }

  const onCopyLink = async () => {
    if (!activeGroupCode) {
      setJoinError('ยังไม่พบกลุ่มสำหรับคัดลอกลิงก์')
      return
    }
    try {
      const result = await shareHelper.copyToClipboard(shareUrl)
      if (result?.success) {
        showCenterToast('คัดลอกลิงก์แล้ว')
        setJoinError('')
        return
      }
      showCenterToast('คัดลอกลิงก์ไม่สำเร็จ')
      setJoinError('')
    } catch {
      showCenterToast('คัดลอกลิงก์ไม่สำเร็จ')
    }
  }

  return (
    <section className="mobile-page party-page party-page-member mx-auto w-full max-w-md">
      <Card className="mobile-panel wealth-card">
        <div className="landing-header px-4 py-4 md:px-6">
          <p className="landing-badge">Big C Receipt Mutelu</p>
          <h1 className="landing-title mt-1 md:text-3xl">{state?.campaign?.party?.campaignName || state?.campaign?.campaignName || 'ตั้งตี้คูณทรัพย์'}</h1>
          <p className="landing-subtitle mt-1">{state?.campaign?.party?.tagline || state?.campaign?.tagline || 'รวมพลังมูเตลู'}</p>
        </div>

        <div className="party-orbit">
          <div className="party-ring" aria-hidden="true" />
          <div className="party-member party-owner">
            <div className="party-avatar party-avatar-owner party-avatar-owner-join">
              <UserAvatar
                src={topOwnerPicture}
                alt={ownerNameWithRole}
                fallback="🙂"
                imageClassName="party-avatar-img"
                priority
              />
            </div>
            <span className="party-label party-label-owner">{ownerNameWithRole}</span>
          </div>
          <div className="party-member party-guest party-guest-1">
            <div className={`party-avatar ${guestLeft ? '' : 'party-avatar-empty'}`}>
              {guestLeft ? (
                <UserAvatar
                  src={getPictureUrl(guestLeft)}
                  alt={getDisplayName(guestLeft, '')}
                  fallback="🙂"
                  imageClassName="party-avatar-img"
                  priority
                />
              ) : '+'}
            </div>
            <span className="party-label">{getDisplayName(guestLeft, '')}</span>
          </div>
          <div className="party-member party-guest party-guest-2">
            <div className="party-avatar party-avatar-self">
              <UserAvatar
                src={centerPicture}
                alt={centerName}
                fallback="👤"
                imageClassName="party-avatar-img"
                priority
              />
            </div>
            <span className="party-label party-label-self">{centerName}</span>
          </div>
          <div className="party-member party-guest party-guest-3">
            <div className={`party-avatar ${guestRight ? '' : 'party-avatar-empty'}`}>
              {guestRight ? (
                <UserAvatar
                  src={getPictureUrl(guestRight)}
                  alt={getDisplayName(guestRight, '')}
                  fallback="🙂"
                  imageClassName="party-avatar-img"
                  priority
                />
              ) : '+'}
            </div>
            <span className="party-label">{getDisplayName(guestRight, '')}</span>
          </div>
        </div>

        <div className="party-info">
          <p className="party-title">ชวนเพื่อน 3 คนเข้า'ตี้' ({membersCount}/4)</p>
          <p className="party-sub">เงื่อนไขสำคัญ : ในตี้ต้องมี 'เพื่อนใหม่' (New User)</p>
          {/* {activeGroupCode ? <p className="party-sub">โค้ดตี้: {activeGroupCode}</p> : null} */}
          {isJoining ? <p className="party-sub">กำลังเชื่อมต่อข้อมูลตี้...</p> : null}
          {requestedGroupCode && !activeGroupCode && !isJoining && joinErrorCode === 'GROUP_NOT_FOUND'
            ? <p className="party-sub party-sub-error">ไม่พบกลุ่มจากลิงก์นี้</p>
            : null}
          {groupError && !isConnectionError(groupError) && !hasPartyData
            ? <p className="party-sub party-sub-error">{normalizeMessage(groupError, 'โหลดสถานะตี้ไม่สำเร็จ')}</p>
            : null}
          {joinError && !isConnectionError(joinError) ? <p className="party-sub party-sub-error">{normalizeMessage(joinError, 'เกิดข้อผิดพลาด')}</p> : null}
          {joinMessage ? <p className="party-sub party-sub-success">{normalizeMessage(joinMessage)}</p> : null}
        </div>

        <div className="party-page-actions">
          <div className="party-share-actions">
            <Button className="btn-uniform wealth-btn party-btn share-line-primary w-full" onClick={onShareLine} disabled={!activeGroupCode}>
              <span className="party-btn-icon party-btn-icon-line" aria-hidden="true">
                <img className="line-share-icon-image" src={lineOfficialIcon} alt="" />
              </span>
              <span>Share LINE</span>
            </Button>
            <Button className="btn-uniform wealth-btn party-btn share-copy-btn w-full" onClick={onCopyLink} disabled={!activeGroupCode}>
              <span>Copy link</span>
            </Button>
          </div>
          <Link to="/game/mutelu/activity" className="party-activity-link">
            <Button className="btn-uniform wealth-btn party-btn party-activity-btn w-full">
              ไปหน้า Activity
            </Button>
          </Link>
        </div>
      </Card>

      <CenterPopup
        open={Boolean(apiPopupTemplate)}
        template={apiPopupTemplate}
        onCancel={() => {
          clearAlreadyInPartyRedirectTimer()
          setRedirectToActivityOnPopupConfirm(false)
          setApiPopupTemplate(null)
          lastApiErrorRef.current = ''
        }}
        onConfirm={() => {
          clearAlreadyInPartyRedirectTimer()
          const shouldRedirect = redirectToActivityOnPopupConfirm
          setRedirectToActivityOnPopupConfirm(false)
          setApiPopupTemplate(null)
          lastApiErrorRef.current = ''
          if (shouldRedirect) {
            navigate('/game/mutelu/activity', { replace: true })
          }
        }}
      />
      <CenterPopup
        open={Boolean(rewardPopupTemplate)}
        template={rewardPopupTemplate}
        onCancel={() => setRewardPopupTemplate(null)}
        onConfirm={() => setRewardPopupTemplate(null)}
      />
      <CenterToast open={Boolean(centerToast)} message={centerToast} />
    </section>
  )
}
